import { MixpanelEventSource, MixpanelSourceContext } from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMultilegDeepDiveRoute, matchTradingPathname } from '../../../../containers/Routes/routes';
import { useAppLayoutConfig } from '../../../../providers/AppLayoutConfig/AppLayoutConfigContext';
import { useAppStateSelector } from '../../../../providers/AppStateProvider';
import type { MultilegPanelConfig } from '../../../../types/LayoutConfig';
import { decodeOptionalRouteParam } from '../../../../utils/decodeRouteParam';
import { Panel } from '../../../Layout/Components/Panel';
import { MultilegCombo } from '../../../MultilegCombo';
import { MultilegComboType } from '../../../MultilegCombo/enums';
import { getNavGroupSlug } from '../../../NavigationSidebar/spacesNavPath';
import { selectCurrentLayout } from '../../AppLayoutSlice';
import { DEFAULT_LAYOUT_ID } from '../../Layouts/tokens';
import type { LayoutPanelProps } from '../../types';

export function MultilegDeepDivePanel({ params, api }: LayoutPanelProps<MultilegPanelConfig>) {
  const {
    resolvedSymbol,
    compactOrderBook,
    instrumentType,
    initiatingLegs,
    haircutRatios,
    customTitle,
    legSymbols,
    legMarketAccounts,
    legMarkets,
  } = params;
  const navigate = useNavigate();
  const location = useLocation();
  const layoutId = useAppStateSelector(selectCurrentLayout)?.id ?? DEFAULT_LAYOUT_ID;
  const layoutSection = useAppStateSelector(state => state.appLayout.layoutSection);
  const { layoutConfigs } = useAppLayoutConfig(layoutSection);
  const navGroupName = useMemo(() => getNavGroupSlug(layoutConfigs, layoutId), [layoutConfigs, layoutId]);
  const editModeRequestKey = useMemo(() => {
    const routeMatch = matchTradingPathname(location.pathname, 'multileg/:instrumentType/:symbol?/:id?');
    const routeSymbol = decodeOptionalRouteParam(routeMatch?.params.symbol);
    const targetsPanel = routeMatch?.params.id === api.id || (!routeMatch?.params.id && routeSymbol === resolvedSymbol);

    return new URLSearchParams(location.search).get('edit') === 'true' && targetsPanel ? location.key : undefined;
  }, [api.id, location, resolvedSymbol]);

  const handleUpdatePanelConfig = useCallback(
    (panelData: Partial<MultilegPanelConfig>, label: string) => {
      api.updateParameters(panelData);
      const newRoute = getMultilegDeepDiveRoute({
        symbol: panelData.resolvedSymbol,
        instrumentType: panelData.instrumentType ?? instrumentType,
        layoutId,
        navGroupName,
        marketAccountNames: panelData.legMarketAccounts,
        marketNames: panelData.legMarkets,
        legSymbols: panelData.legSymbols,
      });
      navigate(newRoute, { replace: true });
      if (!customTitle) {
        api.setTitle(label);
      }
    },
    [navigate, instrumentType, api, customTitle, layoutId, navGroupName]
  );

  return (
    <Panel
      panelApi={api}
      data-testid="multileg-deep-dive-panel"
      children={
        <MixpanelSourceContext.Provider value={MixpanelEventSource.DeepDive}>
          <MultilegCombo
            panelID={api.id}
            compactOrderBook={compactOrderBook}
            instrumentType={instrumentType ?? MultilegComboType.SyntheticCross}
            initiatingLegs={initiatingLegs}
            haircutRatios={haircutRatios}
            resolvedSymbol={resolvedSymbol}
            legSymbols={legSymbols}
            legMarketAccounts={legMarketAccounts}
            legMarkets={legMarkets}
            editModeRequestKey={editModeRequestKey}
            updateMultilegTabData={handleUpdatePanelConfig}
          />
        </MixpanelSourceContext.Provider>
      }
    />
  );
}
